export interface DeliveryAddressInfo {
    firstName: string;
    lastName: string;
    phone?: string;
    address1: string;
    address2?: string | null;
    zipCode: string;
    city: string;
    stateName?: string;
    stateId?: number;
    stateAbbr?: string;
    countryId: number;
    countryIso?: string;
    countryIso3?: string;
    addressType?: number;
    braintreeId?: string;
}

export enum CourierType { // Trade Shipping Options
    Unknown = 0,
    RoyalMail = 1, // "Royal Mail"
    EvriCourier = 2, // "Evri Courier"
    EvriParcelShop = 3, // "Evri ParcelShop"
    Parcelforce = 4, // "Parcelforce"
    Kiosk = 5, // "SMARTDrop Kiosk"
    UPS = 6, // "UPS"
    ASDAToyou = 7, // "ASDA toyou"
}

export interface Courier {
    name: string;
    orderConfirmationMessage: string | null; // string on seller-complete, null on seller-basket
    type: CourierType;
}

export interface ShippingMethod {
    cost: number;
    courier: Courier | null;
    currency: string | null;
    id: number;
    name: string | null;
    selected: boolean;
    shipmentId: number;
    shipmentNumber: string | null;
    shippingCategoryId: number;
    shippingCategoryName: string | null;
    showCountdownTimer: boolean;
    taxCost: number;
}

export enum BasketTypeValue {
    Checkout = 0,
    Rental = 1,
    RentalKeep = 2,
    RentalUpgrade = 3,
}

export interface ActiveBasketSummaryData {
    additionalTaxTotal: number;
    currency: string;
    discount: number;
    email: string;
    id: number;
    items?: Item[];
    number: string;
    paymentTotal: number;
    promoTotal: number;
    shipmentTotal: number;
    status: string;
    subTotal: number;
    term?: number;
    totalItemPrice: number;
    totalPrice: number;
    type: BasketTypeValue;
    userId: number;
}

export interface BasketPaymentMethod {
    id: number;
    type: string;
    name: string;
    description: string;
    active: boolean;
    displayOn: string;
    displayName: string;
    paymentMethodType: PaymentMethodType;
    isAllowed: boolean;
}

export interface BraintreeTokenData {
    clientToken: string;
}

export enum PaymentMethodType {
    Unknown = 0,
    CreditCard = 1,
    PayPal = 2,
    AmazonPay = 3,
    KlarnaPay = 4,
    ApplePay = 5,
    GooglePay = 6,
    DivideBuy = 7,
    BankAccount = 8,
    Charity = 9,
    Stripe = 10,
    PayPalCredit = 1001,
    GoCardless = 1002,
}

export interface ProductProperty {
    createdAt: string;
    id: number;
    name: string;
    position: number;
    presentation: string;
    productId: number;
    updatedAt: string;
    value: string;
}

export interface Taxon {
    id: number;
    parentId?: number;
    permalink?: string;
}

export interface Item {
    id: number;
    name: string;
    grade?: string;
    price: number;
    quantity: number;
    sku?: string;
    shippingCategoryId: number;
    variantId: number;
    thumbnail?: string;
    thumbnailLarge?: string;
    productProperties: ProductProperty[];
    thumbnailAlt?: string;
    category?: string;
    productId: number;
    promoTotal: number;
    isPromoItem: boolean;
    taxons: Taxon[];
    rental: boolean;
    slug: string;
}

export interface Basket {
    summary: ActiveBasketSummaryData;
    shippingMethods: ShippingMethod[];
    softAllocationExpiryTime: string | null;
    disableKlarna: boolean;
    isExtraPromotionApplied: boolean;
    isLoggedIn: boolean;
    itemsJustBeenSaved: boolean;
}

export interface BasketCompleteRequest {
    basketSummary: ActiveBasketSummaryData;
    nonce: string;
    paymentMethod: number;
    storePaymentMethodId: number;
    basketId: number;
    deviceData: string | null;
    forterToken: string | null;
    authorisationStep?: string;
}

export interface CompleteOrderData {
    orderRef: string;
    email: string;
    verificationToken: string;
    validationEvents: ValidationEvent[];
}

export interface ValidationEvent {
    event: string;
    validationResponse: ValidationResponse;
    isApproved: boolean;
}

export interface ValidationResponse {
    stage: string;
    source: string;
    status: string;
    reason: string;
    transactionReference: string;
    paymentMethod: string;
    basketType: string;
    isApproved: boolean;
}

export interface ValidateForterRequest {
    basketId: number;
    paymentMethod: PaymentMethodType;
    forterToken: string;
    authorisationStep: string;
    payPal: ValidateForterRequestPayPal | null;
    billingAddress: DeliveryAddressInfo;
}

export interface ValidateForterRequestPayPal {
    email: string;
}
